import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { WaitlistSignup } from '../components/WaitlistSignup';
import { TicketsSoonNotify } from '../components/TicketsSoonNotify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const stripePromise = process.env.REACT_APP_STRIPE_PK ? loadStripe(process.env.REACT_APP_STRIPE_PK) : null;

function formatEventDate(dateStr, timeStr, endTimeStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  const dayNum = d.getDate();
  const year = d.getFullYear();
  const suffix = dayNum === 1 || dayNum === 21 || dayNum === 31 ? 'st' : dayNum === 2 || dayNum === 22 ? 'nd' : dayNum === 3 || dayNum === 23 ? 'rd' : 'th';

  let result = `${day}, ${month} ${dayNum}${suffix} ${year}`;

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  if (timeStr) {
    result += ` at ${formatTime(timeStr)}`;
    if (endTimeStr) result += ` to ${formatTime(endTimeStr)}`;
  }
  return result;
}

export default function EmbedTicketWidget() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();

  // Read customization from URL params
  const accent = searchParams.get('accent') || '6366f1';
  const showImage = searchParams.get('showImage') !== 'false';
  const bgColor = searchParams.get('bg') || 'ffffff';
  const textColor = searchParams.get('text') || '0f172a';
  const btnText = searchParams.get('btnText') || 'ffffff';

  // UTM passthrough — partners append `?utm_source=newsletter&...` to the
  // widget URL and we forward them into the impression / click / purchase
  // analytics calls so multiple campaigns on the same domain split into
  // separate rows on the Widget Performance dashboard.
  const utm = {
    utm_source:   searchParams.get('utm_source')   || null,
    utm_medium:   searchParams.get('utm_medium')   || null,
    utm_campaign: searchParams.get('utm_campaign') || null,
    utm_content:  searchParams.get('utm_content')  || null,
    utm_id:       searchParams.get('utm_id')       || null,
  };
  // Host-page context injected by the embed snippet's inline script:
  // purl = the page the widget sits on (full URL incl. fbclid/gclid),
  // pref = that page's own referrer (where the visitor came FROM).
  const pageCtx = {
    purl: searchParams.get('purl') || null,
    pref: searchParams.get('pref') || null,
  };

  // Promo-partner attribution helper. Called from the purchase
  // handler (not in render) so we never read document.cookie /
  // localStorage during a render pass — keeps react-hooks/purity
  // happy and avoids stale-closure bugs.
  const readPromoCode = () => {
    let q = searchParams.get('p') || searchParams.get('promo_code');
    // Influencer links point at the PARTNER page (e.g. nocturnalhalloween.com/?p=CODE);
    // the embed snippet forwards that page URL as `purl`, so honor it too.
    if (!q && pageCtx.purl) {
      try { q = new URL(pageCtx.purl).searchParams.get('p') || new URL(pageCtx.purl).searchParams.get('promo_code'); } catch { /* ignore */ }
    }
    if (q) {
      try { localStorage.setItem('eventism_promo', JSON.stringify({ code: q, ts: Date.now() })); } catch { /* ignore */ }
      return q;
    }
    try {
      const m = document.cookie.match(/(?:^|;\s*)eventism_promo=([^;]+)/);
      if (m) return decodeURIComponent(m[1]);
    } catch { /* ignore */ }
    try {
      const ls = JSON.parse(localStorage.getItem('eventism_promo') || 'null');
      if (ls?.code && Date.now() - (ls.ts || 0) < 30 * 24 * 60 * 60 * 1000) {
        return ls.code;
      }
    } catch { /* ignore */ }
    return null;
  };

  // Host domain of the partner page the widget sits on (from purl).
  const hostFromPurl = () => {
    try { return pageCtx.purl ? new URL(pageCtx.purl).hostname.replace('www.', '') : null; } catch { return null; }
  };

  // Kickback referral attribution: ?ref=CODE on the iframe src, or on the
  // partner page URL (forwarded by the embed snippet as `purl`). Persisted
  // 7 days so a friend who comes back later still credits the referrer.
  const readKickbackRef = () => {
    const kbKey = `eventism_kbref_${eventId}`;
    let ref = (searchParams.get('ref') || '').trim().toLowerCase();
    if (!ref && pageCtx.purl) {
      try { ref = (new URL(pageCtx.purl).searchParams.get('ref') || '').trim().toLowerCase(); } catch { /* ignore */ }
    }
    try {
      if (ref) {
        localStorage.setItem(kbKey, JSON.stringify({ code: ref, exp: Date.now() + 7 * 24 * 60 * 60 * 1000 }));
        return ref;
      }
      const raw = localStorage.getItem(kbKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.exp > Date.now()) return parsed.code;
        localStorage.removeItem(kbKey);
      }
    } catch { /* storage blocked */ }
    return ref || null;
  };

  const accentHex = `#${accent}`;
  const bgHex = `#${bgColor}`;
  const textHex = `#${textColor}`;
  const btnTextHex = `#${btnText}`;
  const accentLight = `${accentHex}18`;

  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [postEvent, setPostEvent] = useState(null);
  const [memberBenefits, setMemberBenefits] = useState(null);
  const [feeConfig, setFeeConfig] = useState(null);
  // Narrow-iframe detection: hosts often embed the widget in tight columns,
  // so stack ticket rows vertically instead of squeezing name/desc.
  const [isNarrow, setIsNarrow] = useState(typeof window !== 'undefined' && window.innerWidth < 480);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [selections, setSelections] = useState({});
  const [addonSelections, setAddonSelections] = useState({});
  const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '', phone: '' });
  const [step, setStep] = useState('select');
  const [paymentData, setPaymentData] = useState(null);

  // Cash App Pay (and other redirect methods) bounce back here after approval.
  useEffect(() => {
    const returnOrder = searchParams.get('order_id');
    if (searchParams.get('pay_return') !== '1' || !returnOrder) return;
    if (searchParams.get('redirect_status') === 'failed') {
      toast.error('Payment was not completed — please try again.');
      return;
    }
    (async () => {
      try { await axios.get(`${API}/tickets/confirm/${returnOrder}`); } catch { /* backstops cover it */ }
      setConfirmationData({ orderId: returnOrder, paid: true });
      setStep('confirmation');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [purchasing, setPurchasing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmationData, setConfirmationData] = useState(null);

  // ----------------------------------------------------------------
  // Widget Analytics: fire `impression` once per session on mount and
  // remember the server-assigned session_id so click + purchase events
  // join up server-side. Referrer is the parent page when the widget
  // is iframed; document.referrer is empty when embedded same-origin
  // so we also grab window.parent.location when possible.
  // ----------------------------------------------------------------
  const sessionRef = useRef({ session_id: null });
  const impressionFiredRef = useRef(false);
  const _parentReferrer = () => {
    try {
      return window.parent !== window ? document.referrer : window.location.href;
    } catch {
      return document.referrer || '';
    }
  };

  // First-touch attribution: FB/IG visitors land on the partner homepage
  // with ?fbclid=… but buy after navigating to another page — that second
  // widget load carries no click-id, so it used to classify as "direct".
  // Persist the first meaningful source (7 days, eventism-origin storage)
  // and send it as a fallback with every analytics call.
  const FT_KEY = `eventism_ft_${eventId}`;
  const _hasTrafficSignal = (purlStr, prefStr) => {
    try {
      const q = purlStr ? new URL(purlStr).searchParams : null;
      if (q && ['fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'li_fat_id', 'utm_source'].some((k) => q.get(k))) return true;
    } catch { /* ignore */ }
    return !!prefStr;
  };
  const captureFirstTouch = (purlStr, prefStr) => {
    try {
      if (_hasTrafficSignal(purlStr, prefStr)) {
        localStorage.setItem(FT_KEY, JSON.stringify({ purl: purlStr || '', pref: prefStr || '', exp: Date.now() + 7 * 864e5 }));
        return null;
      }
      const raw = JSON.parse(localStorage.getItem(FT_KEY) || 'null');
      if (raw && raw.exp > Date.now()) return raw;
      if (raw) localStorage.removeItem(FT_KEY);
    } catch { /* storage blocked */ }
    return null;
  };
  const firstTouchRef = useRef(null);

  useEffect(() => {
    if (impressionFiredRef.current || !eventId) return;
    impressionFiredRef.current = true;
    const referrer = _parentReferrer();
    firstTouchRef.current = captureFirstTouch(pageCtx.purl || referrer, pageCtx.pref || '');
    const ft = firstTouchRef.current;
    // Promoter attribution: send the tracking code WITH the impression (one
    // reliable request — a separate POST can be killed when the partner page
    // rebuilds the iframe). The server dedupes repeat loads, so no fragile
    // client-side markers.
    let promoCode = null;
    try {
      const code = readPromoCode();
      const fromLink = searchParams.get('p') || (pageCtx.purl && new URL(pageCtx.purl).searchParams.get('p'));
      if (code && fromLink) promoCode = code;
    } catch { /* ignore */ }
    axios.post(`${API}/widget/impression`, {
      event_id: eventId,
      referrer,
      page_url: pageCtx.purl || referrer,
      page_referrer: pageCtx.pref || '',
      ft_page_url: ft?.purl || null,
      ft_page_referrer: ft?.pref || null,
      promo_tracking_code: promoCode,
      promo_code: readPromoCode() || null,
      embed_utm_source:   utm.utm_source,
      embed_utm_medium:   utm.utm_medium,
      embed_utm_campaign: utm.utm_campaign,
      embed_utm_content:  utm.utm_content,
      embed_utm_id:       utm.utm_id,
    })
      .then((r) => { sessionRef.current.session_id = r.data?.session_id; })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => { fetchData(); }, [eventId]);
  useEffect(() => {
    axios.get(`${API}/events/${eventId}/post-event`).then(r => setPostEvent(r.data)).catch(() => {});
  }, [eventId]);

  useEffect(() => {
    const sendHeight = () => {
      const h = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'eventism-widget-resize', height: h }, '*');
    };
    sendHeight();
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', sendHeight);
    return () => { observer.disconnect(); window.removeEventListener('resize', sendHeight); };
  }, [step, selections, ticketTypes]);

  // Hide body margin/padding for clean embed
  useEffect(() => {
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
  }, []);

  const fetchData = async () => {
    try {
      // Direct single-event lookup — was previously listing ?limit=100 and
      // filtering client-side, which silently broke for any event ranked
      // beyond position #100 in the global feed.
      const evRes = await axios.get(`${API}/hosted-events/${eventId}`);
      const ev = evRes.data?.event;
      if (!ev) { setError('Event not found'); setLoading(false); return; }
      setEvent(ev);
      const ttRes = await axios.get(`${API}/events/${eventId}/ticket-types`);
      setTicketTypes((ttRes.data.ticket_types || []).filter(tt => tt.is_active));
      setMemberBenefits(ttRes.data.member_benefits || null);
      setFeeConfig(ttRes.data.fee_config || null);
    } catch (err) {
      if (err?.response?.status === 404) setError('Event not found');
      else setError('Failed to load event');
    }
    finally { setLoading(false); }
  };

  const updateQty = (ttId, delta) => {
    setSelections(prev => {
      const tt = ticketTypes.find(t => t.ticket_type_id === ttId);
      const current = prev[ttId] || 0;
      const minQty = Math.max(1, tt?.min_per_order || 1);
      const max = Math.min(tt?.max_per_order || 10, (tt?.quantity_total || 0) - (tt?.quantity_sold || 0));
      let next = current + delta;
      // Snap up to min on the first +click so the buyer doesn't have to
      // tap "+" 5 times for a "minimum 5" tier.
      if (delta > 0 && current === 0 && minQty > 1) next = Math.min(max, minQty);
      // Going below the minimum (but above zero) drops to zero so the
      // tier is effectively cleared.
      if (delta < 0 && next > 0 && next < minQty) next = 0;
      return { ...prev, [ttId]: Math.max(0, Math.min(max, next)) };
    });
  };

  const totalTickets = Object.values(selections).reduce((s, v) => s + v, 0);

  // ---- Add-ons ----
  const regularTypes = ticketTypes.filter(tt => !tt.is_addon);
  const addonTypes = ticketTypes.filter(tt => tt.is_addon);
  const addonRows = addonTypes.flatMap(tt =>
    (tt.variations || []).length > 0
      ? tt.variations.map(v => ({ key: `${tt.ticket_type_id}::${v.id}`, tt, variation: v, price: v.price }))
      : [{ key: tt.ticket_type_id, tt, variation: null, price: tt.price }]
  );
  // Tickets in the cart this add-on applies to (empty list = all ticket types)
  const addonEligibleTickets = (tt) => {
    const allowed = tt.applies_to_ticket_types || [];
    if (allowed.length === 0) return totalTickets;
    return allowed.reduce((s, id) => s + (selections[id] || 0), 0);
  };
  const updateAddonQty = (row, delta) => {
    setAddonSelections(prev => {
      const current = prev[row.key] || 0;
      const available = (row.tt.quantity_total || 0) - (row.tt.quantity_sold || 0);
      const siblingQty = Object.entries(prev).reduce((s, [k, q]) =>
        k !== row.key && k.startsWith(row.tt.ticket_type_id) ? s + q : s, 0);
      const eligible = addonEligibleTickets(row.tt);
      const perOrderCap = Math.max(row.tt.max_per_order || 10, eligible);
      const max = Math.max(0, Math.min(perOrderCap, available - siblingQty));
      // First "Add" defaults to one upgrade per ticket — buyers can step back down
      const next = current === 0 && delta > 0 ? Math.max(1, Math.min(eligible, max)) : current + delta;
      return { ...prev, [row.key]: Math.max(0, Math.min(max, next)) };
    });
  };
  const totalAddonUnits = Object.values(addonSelections).reduce((s, v) => s + v, 0);
  const addonPrice = addonRows.reduce((sum, row) => sum + row.price * (addonSelections[row.key] || 0), 0);

  useEffect(() => {
    setAddonSelections(prev => {
      if (!Object.keys(prev).length) return prev;
      if (totalTickets === 0) return {};
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(next)) {
        const tt = addonTypes.find(t => key.startsWith(t.ticket_type_id));
        if (tt && (tt.applies_to_ticket_types || []).length > 0 && addonEligibleTickets(tt) === 0) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  const totalPrice = ticketTypes.reduce((sum, tt) => sum + (tt.price * (selections[tt.ticket_type_id] || 0)), 0) + addonPrice;

  // Processing fee — shown up-front so the price never jumps at Stripe.
  // Mirrors backend calculate_fee_cents (percent + flat/ticket + processing %).
  const calcFeeCents = (subtotalDollars, qty) => {
    if (!feeConfig?.buyer_pays || subtotalDollars <= 0) return 0;
    const sc = Math.round(subtotalDollars * 100);
    return Math.round(sc * (feeConfig.percent || 0) / 100)
      + (feeConfig.flat_cents || 0) * qty
      + Math.round(sc * (feeConfig.payment_processing_percent || 0) / 100);
  };
  const withFee = (price) => (price > 0 ? price + calcFeeCents(price, 1) / 100 : price);
  const orderFee = totalPrice > 0 ? calcFeeCents(totalPrice, totalTickets + totalAddonUnits) / 100 : 0;
  const grandTotal = totalPrice + orderFee;

  const handleCheckout = async () => {
    if (!buyerInfo.name || !buyerInfo.email) { toast.error('Please enter your name and email'); return; }
    const items = Object.entries(selections).filter(([_, qty]) => qty > 0).map(([ttId, qty]) => ({ ticket_type_id: ttId, quantity: qty }));
    if (items.length === 0) return;
    Object.entries(addonSelections).filter(([_, qty]) => qty > 0).forEach(([key, qty]) => {
      const [ttId, varId] = key.split('::');
      items.push({ ticket_type_id: ttId, quantity: qty, variation_id: varId || undefined });
    });
    // Widget Analytics: fire click. Best-effort, don't block checkout.
    try {
      axios.post(`${API}/widget/click`, {
        event_id: eventId,
        referrer: _parentReferrer(),
        page_url: pageCtx.purl || _parentReferrer(),
        page_referrer: pageCtx.pref || '',
        ft_page_url: firstTouchRef.current?.purl || null,
        ft_page_referrer: firstTouchRef.current?.pref || null,
        session_id: sessionRef.current.session_id,
        promo_code: readPromoCode() || null,
        embed_utm_source:   utm.utm_source,
        embed_utm_medium:   utm.utm_medium,
        embed_utm_campaign: utm.utm_campaign,
        embed_utm_content:  utm.utm_content,
        embed_utm_id:       utm.utm_id,
      });
    } catch { /* best-effort */ }
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/purchase`, {
        items,
        name: buyerInfo.name,
        email: buyerInfo.email,
        phone: buyerInfo.phone,
        // Attribution payload — server stores on ticket_order so the
        // Stripe webhook can fire the conversion event back to
        // widget_views once payment completes.
        embed_source: 'widget',
        embed_page_host: hostFromPurl() || undefined,
        embed_host: (() => {
          try { return new URL(_parentReferrer()).hostname || 'direct'; }
          catch { return 'direct'; }
        })(),
        embed_session_id: sessionRef.current.session_id,
        embed_utm_source:   utm.utm_source,
        embed_utm_medium:   utm.utm_medium,
        embed_utm_campaign: utm.utm_campaign,
        embed_utm_content:  utm.utm_content,
        embed_utm_id:       utm.utm_id,
        // Forward the promo-partner tracking code so the Stripe
        // webhook can credit the partner's wallet when the order
        // completes (see credit_promo_commission in promo_partners.py).
        promo_code: readPromoCode() || undefined,
        // Kickback referral — credits the friend who shared their link.
        kickback_ref: readKickbackRef() || undefined,
        // In-page express checkout (card/Google Pay/Cash App/Link) — no
        // redirect, no popup. Falls back to hosted Checkout if unavailable.
        payment_ui: stripePromise && totalPrice > 0 ? 'embedded' : undefined,
      });
      if (res.data.free) {
        setConfirmationData({ orderId: res.data.order_id, tickets: res.data.tickets, free: true });
        setStep('confirmation');
      } else if (res.data.client_secret) {
        setPaymentData({ clientSecret: res.data.client_secret, orderId: res.data.order_id, amountCents: res.data.amount_cents });
        setStep('payment');
      } else if (res.data.checkout_url) {
        // Hosted fallback — try a new window, then top-level redirect, and the
        // confirmation screen always carries a tap-to-pay link as safety net.
        let opened = false;
        try { opened = !!window.open(res.data.checkout_url, '_blank'); } catch { opened = false; }
        if (!opened) {
          try { window.top.location.href = res.data.checkout_url; opened = true; } catch { opened = false; }
        }
        setConfirmationData({ orderId: res.data.order_id, pending: true, checkoutUrl: res.data.checkout_url, windowOpened: opened });
        setStep('confirmation');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    }
    finally { setPurchasing(false); }
  };

  const handlePaymentSuccess = async (orderId) => {
    // Complete immediately (webhook + reconciler are the backstops).
    try { await axios.get(`${API}/tickets/confirm/${orderId}`); } catch { /* backstops cover it */ }
    setConfirmationData({ orderId, paid: true });
    setStep('confirmation');
    setPaymentData(null);
  };

  const shell = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', width: '100%', maxWidth: 560, margin: '0 auto', background: bgHex, borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', boxSizing: 'border-box', color: textHex };
  const inputSt = { width: '100%', padding: '11px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: bgHex, color: textHex };

  if (loading) return <div style={shell}><div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}><div style={{ width: 28, height: 28, border: `3px solid #e2e8f0`, borderTopColor: accentHex, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style></div></div>;
  if (error) return <div style={shell}><div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>{error}</div></div>;

  const dateStr = formatEventDate(event?.event_date, event?.event_time, event?.end_time);
  const venueRest = [event?.venue_address, event?.venue_city && event?.venue_state ? `${event.venue_city}, ${event.venue_state}` : ''].filter(Boolean).join(' · ');
  const citySlug = (event?.venue_city || event?.city || '').toLowerCase().trim().replace(/\s+/g, '-');
  const venueUrl = event?.venue_slug && citySlug ? `${BASE_URL}/${citySlug}/venues/${event.venue_slug}` : null;
  const organizerUrl = event?.organizer?.slug ? `${BASE_URL}/organizations/${event.organizer.slug}` : null;
  const linkStyle = { color: accentHex, textDecoration: 'none', fontWeight: 600 };

  return (
    <div style={shell}>
      <Toaster position="top-center" richColors />

      {/* Event Image */}
      {showImage && event?.image_url && (
        <div style={{ width: '100%', height: 160, overflow: 'hidden', position: 'relative' }}>
          <img src={event.image_url} alt={event.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: `linear-gradient(transparent, ${bgHex})` }} />
        </div>
      )}

      {/* Event Header */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e2e8f0' }}>
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: textHex, lineHeight: 1.3 }}>{event?.name}</h2>
        {dateStr && <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>{dateStr}</p>}
        {(event?.venue_name || venueRest || event?.organizer?.name) && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
            {event?.venue_name && (
              venueUrl ? (
                <a href={venueUrl} target="_blank" rel="noopener noreferrer" style={linkStyle} data-testid="embed-venue-link">{event.venue_name}</a>
              ) : (
                <span>{event.venue_name}</span>
              )
            )}
            {event?.venue_name && venueRest ? ' · ' : ''}
            {venueRest}
            {event?.organizer?.name && (
              <>
                {(event?.venue_name || venueRest) ? ' · ' : ''}
                {/* inline-block keeps "Hosted by Name" together as one unit,
                    so it shares the venue line and only wraps as a whole
                    when the line runs out of room. */}
                <span style={{ display: 'inline-block' }} data-testid="embed-hosted-by">
                  Hosted by{' '}
                  {organizerUrl ? (
                    <a href={organizerUrl} target="_blank" rel="noopener noreferrer" style={linkStyle} data-testid="embed-organizer-link">{event.organizer.name}</a>
                  ) : (
                    <span style={{ fontWeight: 600 }}>{event.organizer.name}</span>
                  )}
                </span>
              </>
            )}
          </p>
        )}
      </div>

      {/* Step: Select Tickets */}
      {step === 'select' && (
        <div style={{ padding: '14px 20px 16px' }}>
          {memberBenefits && (memberBenefits.ticket_discount_percent > 0 || memberBenefits.early_access_hours > 0) && (
            <div
              style={{
                padding: '10px 12px', marginBottom: 12, borderRadius: 8,
                background: 'linear-gradient(90deg, #fff7ed, #fef3c7)',
                border: '1px solid #fcd34d', color: '#92400e', fontSize: 13,
              }}
              data-testid="embed-member-banner"
            >
              <strong>Member benefits applied · </strong>
              {memberBenefits.ticket_discount_percent > 0 && (
                <span>{memberBenefits.ticket_discount_percent}% off every tier</span>
              )}
              {memberBenefits.early_access_hours > 0 && (
                <span> · {memberBenefits.early_access_hours}h early access</span>
              )}
            </div>
          )}
          {postEvent?.ended ? (
            <div style={{ textAlign: 'center', padding: '28px 12px' }} data-testid="embed-event-ended">
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>This event has ended 🎉</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 6 }}>Thanks to everyone who came out — don't miss the next one.</div>
              {postEvent.next_event ? (
                <a href={postEvent.next_event.url} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-block', marginTop: 14, background: accentHex, color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 13, padding: '11px 20px', borderRadius: 9 }}
                  data-testid="embed-next-event">
                  Next up: {postEvent.next_event.name} · {postEvent.next_event.event_date} →
                </a>
              ) : null}
              <div style={{ marginTop: 12, fontSize: 12 }}>
                {postEvent.organizer && (
                  <a href={postEvent.organizer.url} target="_blank" rel="noreferrer" style={{ color: accentHex, fontWeight: 600, textDecoration: 'none' }} data-testid="embed-follow-organizer">
                    Follow {postEvent.organizer.name}
                  </a>
                )}
                {postEvent.organizer && postEvent.venue && <span style={{ color: '#cbd5e1' }}> · </span>}
                {postEvent.venue && (
                  <a href={postEvent.venue.url} target="_blank" rel="noreferrer" style={{ color: accentHex, fontWeight: 600, textDecoration: 'none' }} data-testid="embed-follow-venue">
                    Follow {postEvent.venue.name}
                  </a>
                )}
              </div>
            </div>
          ) : ticketTypes.length === 0 ? (
            <TicketsSoonNotify eventId={eventId} compact />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {regularTypes.length > 0 && regularTypes.every(t => (t.quantity_total - (t.quantity_sold || 0)) <= 0) && (
                <WaitlistSignup eventId={eventId} compact />
              )}
              {regularTypes.map(tt => {
                const available = tt.quantity_total - (tt.quantity_sold || 0);
                const qty = selections[tt.ticket_type_id] || 0;
                const soldOut = available <= 0;
                const memberPrice = tt.current_price_info?.member_price;
                const showMemberPrice = memberPrice != null && memberPrice !== tt.price;
                return (
                  <div key={tt.ticket_type_id} style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', alignItems: isNarrow ? 'stretch' : 'center', gap: isNarrow ? 10 : 12, padding: '14px 16px', border: `1px solid ${qty > 0 ? accentHex : '#e2e8f0'}`, borderRadius: 10, background: soldOut ? '#f8fafc' : qty > 0 ? accentLight : bgHex, opacity: soldOut ? 0.55 : 1, transition: 'border-color 0.2s, background 0.2s' }} data-testid={`embed-tt-${tt.ticket_type_id}`}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 15, color: textHex }}>{tt.name}</span>
                        {tt.tier !== 'general' && <span style={{ fontSize: 10, padding: '2px 6px', background: '#f1f5f9', borderRadius: 4, color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>{tt.tier}</span>}
                        {tt.visibility === 'members_only' && <span style={{ fontSize: 10, padding: '2px 6px', background: '#fef3c7', borderRadius: 4, color: '#92400e', textTransform: 'uppercase', fontWeight: 600 }} data-testid={`embed-tt-members-only-${tt.ticket_type_id}`}>Members only</span>}
                      </div>
                      {tt.description && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>{tt.description}</p>}
                      {tt.perks?.length > 0 && <p style={{ margin: '3px 0 0', fontSize: 11, color: accentHex }}>{tt.perks.join(' · ')}</p>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: isNarrow ? 'space-between' : 'flex-end', gap: 12, paddingTop: isNarrow ? 8 : 0, borderTop: isNarrow ? '1px solid #f1f5f9' : 'none' }}>
                      <div style={{ textAlign: isNarrow ? 'left' : 'right', minWidth: 65 }}>
                        {showMemberPrice ? (
                          <>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#16a34a' }} data-testid={`embed-tt-member-price-${tt.ticket_type_id}`}>
                              ${memberPrice}
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'line-through' }}>${tt.price}</div>
                          </>
                        ) : (
                          <div style={{ fontWeight: 700, fontSize: 16, color: textHex }} data-testid={`embed-tt-price-${tt.ticket_type_id}`}>{tt.price === 0 ? 'Free' : `$${tt.price}`}</div>
                        )}
                        {!soldOut && tt.show_remaining !== false && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{available} left</div>}
                        {!soldOut && (tt.min_per_order || 1) > 1 && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>min {tt.min_per_order}</div>}
                        {soldOut && <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Sold Out</div>}
                      </div>
                      {!soldOut && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <button onClick={() => updateQty(tt.ticket_type_id, -1)} disabled={qty === 0} style={{ width: isNarrow ? 38 : 32, height: isNarrow ? 38 : 32, border: '1px solid #e2e8f0', borderRadius: 7, background: bgHex, cursor: qty === 0 ? 'default' : 'pointer', fontSize: 18, color: qty === 0 ? '#cbd5e1' : textHex, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid={`embed-minus-${tt.ticket_type_id}`}>-</button>
                          <span style={{ width: 28, textAlign: 'center', fontWeight: 700, fontSize: 15 }}>{qty}</span>
                          <button onClick={() => updateQty(tt.ticket_type_id, 1)} style={{ width: isNarrow ? 38 : 32, height: isNarrow ? 38 : 32, border: `1px solid ${accentHex}`, borderRadius: 7, background: accentHex, cursor: 'pointer', fontSize: 18, color: btnTextHex, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid={`embed-plus-${tt.ticket_type_id}`}>+</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {addonRows.length > 0 && totalTickets > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${accentHex}66` }} data-testid="embed-addons-section">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: accentHex, textTransform: 'uppercase', letterSpacing: 1 }}>✦ Enhance your experience</span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>optional extras — grab one per ticket</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {addonRows.map(row => {
                  const available = (row.tt.quantity_total || 0) - (row.tt.quantity_sold || 0);
                  const soldOut = available <= 0;
                  const qty = addonSelections[row.key] || 0;
                  const label = row.variation ? `${row.tt.name} — ${row.variation.name}` : row.tt.name;
                  const allowed = row.tt.applies_to_ticket_types || [];
                  const allowedNames = allowed.map(id => regularTypes.find(t => t.ticket_type_id === id)?.name).filter(Boolean);
                  const locked = allowed.length > 0 && addonEligibleTickets(row.tt) === 0;
                  return (
                    <div key={row.key} style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', alignItems: isNarrow ? 'stretch' : 'center', gap: isNarrow ? 8 : 12, padding: '11px 14px', border: `1.5px dashed ${qty > 0 ? accentHex : `${accentHex}55`}`, borderRadius: 10, background: soldOut || locked ? '#f8fafc' : qty > 0 ? accentLight : 'transparent', opacity: soldOut || locked ? 0.55 : 1 }} data-testid={`embed-addon-${row.key}`}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 600, fontSize: 13.5, color: textHex }}>{label}</span>
                          <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: accentHex, border: `1px solid ${accentHex}66`, borderRadius: 4, padding: '1px 5px' }}>Add-on</span>
                        </div>
                        {row.tt.description && !row.variation && <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>{row.tt.description}</p>}
                        {allowedNames.length > 0 && <p style={{ margin: '2px 0 0', fontSize: 10, color: accentHex }} data-testid={`embed-addon-applies-${row.key}`}>Available with: {allowedNames.join(', ')}</p>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: isNarrow ? 'space-between' : 'flex-end', gap: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: accentHex, minWidth: 50, textAlign: isNarrow ? 'left' : 'right' }}>{row.price === 0 ? 'Free' : `+$${row.price}`}</div>
                        {soldOut ? (
                          <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Sold Out</div>
                        ) : locked ? (
                          <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 600 }} data-testid={`embed-addon-locked-${row.key}`}>Requires {allowedNames.join(' or ')}</div>
                        ) : qty === 0 ? (
                          <button onClick={() => updateAddonQty(row, 1)} style={{ height: isNarrow ? 36 : 30, padding: '0 14px', border: `1.5px solid ${accentHex}`, borderRadius: 18, background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: accentHex }} data-testid={`embed-addon-plus-${row.key}`}>+ Add</button>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <button onClick={() => updateAddonQty(row, -1)} style={{ width: isNarrow ? 36 : 30, height: isNarrow ? 36 : 30, border: '1px solid #e2e8f0', borderRadius: 18, background: bgHex, cursor: 'pointer', fontSize: 16, color: textHex, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid={`embed-addon-minus-${row.key}`}>-</button>
                            <span style={{ width: 26, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>{qty}</span>
                            <button onClick={() => updateAddonQty(row, 1)} style={{ width: isNarrow ? 36 : 30, height: isNarrow ? 36 : 30, border: `1px solid ${accentHex}`, borderRadius: 18, background: accentHex, cursor: 'pointer', fontSize: 16, color: btnTextHex, display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid={`embed-addon-plus-${row.key}`}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {totalTickets > 0 && (
            <div style={{ marginTop: 14, padding: '14px 18px', background: accentHex, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setStep('checkout')} data-testid="embed-checkout-btn">
              <div><span style={{ color: btnTextHex, fontWeight: 700, fontSize: 15 }}>Get Tickets</span><span style={{ color: `${btnTextHex}bb`, fontSize: 13, marginLeft: 8 }}>{totalTickets} ticket{totalTickets > 1 ? 's' : ''}{totalAddonUnits > 0 ? ` + ${totalAddonUnits} add-on${totalAddonUnits > 1 ? 's' : ''}` : ''}</span></div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: btnTextHex, fontWeight: 700, fontSize: 17 }} data-testid="embed-bar-total">{grandTotal === 0 ? 'Free' : `$${grandTotal.toFixed(2)}`}</span>
                {orderFee > 0 && <div style={{ color: `${btnTextHex}99`, fontSize: 9, marginTop: 1 }}>incl. fees</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step: Checkout */}
      {step === 'checkout' && (
        <div style={{ padding: '14px 20px 16px' }}>
          <button onClick={() => setStep('select')} style={{ background: 'none', border: 'none', color: accentHex, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, fontWeight: 500 }} data-testid="embed-back-btn">&larr; Back to tickets</button>
          <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Order Summary</div>
            {ticketTypes.filter(tt => (selections[tt.ticket_type_id] || 0) > 0).map(tt => (
              <div key={tt.ticket_type_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                <span>{selections[tt.ticket_type_id]}x {tt.name}</span>
                <span style={{ fontWeight: 600 }}>{tt.price === 0 ? 'Free' : `$${(tt.price * selections[tt.ticket_type_id]).toFixed(2)}`}</span>
              </div>
            ))}
            {addonRows.filter(row => (addonSelections[row.key] || 0) > 0).map(row => (
              <div key={row.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }} data-testid={`embed-summary-addon-${row.key}`}>
                <span>{addonSelections[row.key]}x {row.variation ? `${row.tt.name} — ${row.variation.name}` : row.tt.name} <span style={{ color: '#94a3b8' }}>(add-on)</span></span>
                <span style={{ fontWeight: 600 }}>{row.price === 0 ? 'Free' : `$${(row.price * addonSelections[row.key]).toFixed(2)}`}</span>
              </div>
            ))}
            {orderFee > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: '#64748b' }} data-testid="embed-summary-fee">
                <span>Processing fee</span>
                <span style={{ fontWeight: 600 }}>${orderFee.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 8 }}>
              <span>Total</span><span data-testid="embed-summary-total">{grandTotal === 0 ? 'Free' : `$${grandTotal.toFixed(2)}`}</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: textHex, display: 'block', marginBottom: 4 }}>Full Name *</label><input value={buyerInfo.name} onChange={e => setBuyerInfo(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" style={inputSt} data-testid="embed-buyer-name" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: textHex, display: 'block', marginBottom: 4 }}>Email *</label><input type="email" value={buyerInfo.email} onChange={e => setBuyerInfo(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" style={inputSt} data-testid="embed-buyer-email" /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: textHex, display: 'block', marginBottom: 4 }}>Phone <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label><input value={buyerInfo.phone} onChange={e => setBuyerInfo(p => ({ ...p, phone: e.target.value }))} placeholder="555-123-4567" style={inputSt} data-testid="embed-buyer-phone" /></div>
          </div>
          <button onClick={handleCheckout} disabled={purchasing || !buyerInfo.name || !buyerInfo.email} style={{ marginTop: 14, width: '100%', padding: '14px 0', background: purchasing ? '#a5b4fc' : accentHex, color: btnTextHex, border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: purchasing ? 'wait' : 'pointer' }} data-testid="embed-purchase-btn">
            {purchasing ? 'Processing...' : grandTotal === 0 ? 'Confirm Free Tickets' : `Pay $${grandTotal.toFixed(2)}`}
          </button>
        </div>
      )}

      {/* Step: Payment (embedded express checkout) */}
      {step === 'payment' && paymentData && stripePromise && (
        <div style={{ padding: '18px 20px' }}>
          <button onClick={() => { setStep('checkout'); setPaymentData(null); }} style={{ background: 'none', border: 'none', color: accentHex, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12, fontWeight: 500 }} data-testid="embed-pay-back-btn">&larr; Back</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: textHex }}>Total due</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: textHex }} data-testid="embed-pay-total">${(paymentData.amountCents / 100).toFixed(2)}</span>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret: paymentData.clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: accentHex, borderRadius: '8px' } } }}>
            <EmbeddedPayForm
              amountCents={paymentData.amountCents}
              accentHex={accentHex}
              btnTextHex={btnTextHex}
              returnUrl={`${BASE_URL}/embed/tickets/${eventId}?pay_return=1&order_id=${paymentData.orderId}`}
              onSuccess={() => handlePaymentSuccess(paymentData.orderId)}
            />
          </Elements>
          <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>Secure 256-bit payment powered by Stripe. Tickets are emailed instantly.</p>
        </div>
      )}

      {/* Step: Confirmation */}
      {step === 'confirmation' && confirmationData && (
        <div style={{ padding: '28px 20px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: textHex }}>{(confirmationData.free || confirmationData.paid) ? 'Tickets Confirmed!' : confirmationData.windowOpened ? 'Payment Opened' : 'One More Step'}</h3>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748b' }}>{confirmationData.free ? 'Check your email for your tickets and QR codes.' : confirmationData.paid ? 'Payment received — your tickets and QR codes are on the way to your email.' : confirmationData.windowOpened ? 'Complete payment in the new window. Tickets will be emailed.' : 'Tap below to open the secure payment page. Tickets will be emailed.'}</p>
          {!confirmationData.free && !confirmationData.paid && confirmationData.checkoutUrl && (
            <a
              href={confirmationData.checkoutUrl}
              target="_top"
              rel="noopener"
              style={{ display: 'block', marginTop: 16, padding: '14px 0', background: accentHex, color: btnTextHex, borderRadius: 10, fontSize: 15, fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}
              data-testid="embed-complete-payment-link"
            >
              {confirmationData.windowOpened ? 'Payment window didn\u2019t open? Tap here' : 'Complete Payment \u2192'}
            </a>
          )}
          <p style={{ margin: '10px 0 0', fontSize: 12, color: '#94a3b8' }}>Order: {confirmationData.orderId}</p>
          <button onClick={() => { setStep('select'); setSelections({}); setAddonSelections({}); setBuyerInfo({ name: '', email: '', phone: '' }); setConfirmationData(null); fetchData(); }} style={{ marginTop: 16, padding: '10px 24px', background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: textHex }}>Get More Tickets</button>
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: '10px 20px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: '#b0b8c4' }}>Ticketing Powered by</span>
        <a href={BASE_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} data-testid="embed-eventism-link">
          <div style={{ height: 16, width: 100, backgroundColor: accentHex, WebkitMaskImage: `url(${BASE_URL}/eventism-logo.png)`, WebkitMaskSize: 'contain', WebkitMaskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskImage: `url(${BASE_URL}/eventism-logo.png)`, maskSize: 'contain', maskRepeat: 'no-repeat', maskPosition: 'center' }} />
        </a>
      </div>
    </div>
  );
}


function EmbeddedPayForm({ amountCents, accentHex, btnTextHex, returnUrl, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [expressReady, setExpressReady] = useState(false);

  const confirm = async () => {
    if (!stripe || !elements || paying) return;
    setPaying(true);
    setPayError('');
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });
    if (error) {
      setPayError(error.message || 'Payment failed — please try again.');
      setPaying(false);
      return;
    }
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onSuccess();
    } else {
      setPaying(false);
    }
  };

  return (
    <div data-testid="embed-pay-form">
      <ExpressCheckoutElement
        options={{ paymentMethods: { applePay: 'never', googlePay: 'always' } }}
        onReady={({ availablePaymentMethods }) => setExpressReady(!!availablePaymentMethods)}
        onConfirm={confirm}
      />
      {expressReady && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0' }}>
          <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
          <span style={{ fontSize: 11, color: '#94a3b8' }}>or pay another way</span>
          <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
        </div>
      )}
      <PaymentElement options={{ wallets: { applePay: 'never', googlePay: 'never' } }} />
      {payError && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#dc2626' }} data-testid="embed-pay-error">{payError}</p>}
      <button
        onClick={confirm}
        disabled={paying || !stripe}
        style={{ marginTop: 14, width: '100%', padding: '14px 0', background: paying ? '#a5b4fc' : accentHex, color: btnTextHex, border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: paying ? 'wait' : 'pointer' }}
        data-testid="embed-pay-now-btn"
      >
        {paying ? 'Processing…' : `Pay $${(amountCents / 100).toFixed(2)}`}
      </button>
    </div>
  );
}
